import { Outlet, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { LangSwitcher } from '@/components/LangSwitcher'
import { SaleTopbar } from '@/components/SaleTopbar'

export function RootLayout() {
  const { t } = useTranslation()
  const year = new Date().getFullYear()
  return (
    <div className="min-h-full flex flex-col bg-white text-slate-900">
      {/* Sale banner + site nav share one sticky wrapper — two sibling `sticky top-0`
          elements would stack on top of each other and hide the banner on scroll. */}
      <div className="sticky top-0 z-30">
        <SaleTopbar />
        <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
            <Link to="/" className="flex items-center gap-2 text-lg font-bold tracking-tight text-[#0d3b66]">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#0d3b66] text-amber-400">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                  <path d="M5 4h7a5 5 0 0 1 0 10H9v6H5V4zm4 3v4h3a2 2 0 1 0 0-4H9z" />
                </svg>
              </span>
              avtoturargoh.uz
            </Link>
            <nav className="hidden items-center gap-6 text-sm font-medium md:flex">
              <a href="/#services" className="text-slate-600 hover:text-[#0d3b66]">
                {t('nav.services')}
              </a>
              <a href="/#features" className="text-slate-600 hover:text-[#0d3b66]">
                {t('nav.permits')}
              </a>
              <a href="/#download" className="text-slate-600 hover:text-[#0d3b66]">
                {t('nav.download')}
              </a>
              <Link to="/map" className="text-slate-600 hover:text-[#0d3b66]">
                {t('nav.map')}
              </Link>
            </nav>
            <div className="flex items-center gap-3">
              <LangSwitcher />
              <Link
                to="/login"
                className="rounded-lg bg-[#0d3b66] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2540]"
              >
                {t('nav.login')}
              </Link>
            </div>
          </div>
        </header>
      </div>
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-12">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="flex items-center gap-2 text-base font-bold text-[#0d3b66]">
                <span className="grid h-7 w-7 place-items-center rounded-md bg-[#0d3b66] text-amber-400">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                    <path d="M5 4h7a5 5 0 0 1 0 10H9v6H5V4zm4 3v4h3a2 2 0 1 0 0-4H9z" />
                  </svg>
                </span>
                avtoturargoh.uz
              </div>
              <p className="mt-3 text-sm text-slate-600">{t('home.sub')}</p>
            </div>
            <FooterCol title={t('home.footer_product')}>
              <a href="/#services">{t('nav.services')}</a>
              <a href="/#features">{t('nav.permits')}</a>
              <a href="/#download">{t('nav.download')}</a>
              <Link to="/map">{t('nav.map')}</Link>
            </FooterCol>
            <FooterCol title={t('home.footer_company')}>
              <a href="#">{t('home.footer_about')}</a>
              <a href="#">{t('home.footer_careers')}</a>
              <a href="#">{t('home.footer_news')}</a>
              <a href="#">{t('home.footer_support')}</a>
            </FooterCol>
            <FooterCol title={t('home.footer_legal')}>
              <a href="#">{t('home.footer_terms')}</a>
              <a href="#">{t('home.footer_privacy')}</a>
              <a href="#">{t('home.footer_offer')}</a>
            </FooterCol>
          </div>
          <div className="mt-10 border-t border-slate-200 pt-6 text-xs text-slate-500">
            © {year} avtoturargoh.uz · turargoh.uz — {t('home.footer_rights')}
          </div>
        </div>
      </footer>
    </div>
  )
}

function FooterCol({ title, children }: { title: string; children: React.ReactNode }) {
  const links = (Array.isArray(children) ? children : [children]) as React.ReactNode[]
  return (
    <div>
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      <ul className="mt-3 space-y-2 text-sm text-slate-600">
        {links.map((node, i) => (
          <li key={i} className="[&>a]:hover:text-[#0d3b66] [&>a]:transition">
            {node}
          </li>
        ))}
      </ul>
    </div>
  )
}
